import type { ReactNode } from 'react';

/**
 * A name somebody typed, rendered so it cannot rearrange the text around it.
 *
 * It is a `<bdi>` and nothing else. `<bdi>` means "bidirectional isolate": the browser
 * works out the direction of what is inside it on its own, and — this is the part that
 * matters — whatever it decides stops at the element's edges. Text either side of it is
 * laid out as if the contents were a single neutral object.
 *
 * ## Why a user-entered name needs that
 *
 * Two different problems, one containment (steward-app#600).
 *
 * The **deception** case is a member storing a RIGHT-TO-LEFT OVERRIDE inside a name.
 * Everything after it renders backwards, so `statement<RLO>fdp.exe` reads on screen as
 * `statementexe.pdf`, and because nothing isolated it, the reversal ran on past the end
 * of the name and into the row beside it. The façade now strips those characters coming
 * in and going out, so this is not the pass carrying that weight — but it is what keeps
 * a name from reaching outside itself if one ever gets through, and the SPA can render
 * from a React Query cache an older bundle filled.
 *
 * The **ordinary** case needs no attacker at all, and is the better reason this exists.
 * A name written in Arabic or Hebrew is right-to-left by its own nature, so the bidi
 * algorithm reorders the neutral characters around it — a comma, a currency figure, the
 * `(you)` after a member's name, the file size in brackets after a document. Without an
 * isolate those pieces migrate to the wrong side of the name and the row reads wrong.
 * A family with an Israeli account or an Arabic-named entity hits that on day one, with
 * nobody doing anything hostile at all.
 *
 * ## Where to use it, and where not
 *
 * Use it around **text a person typed**: an account, entity, asset, liability, contact,
 * folder, credential or vault name, a document title, an institution, a member's display
 * name, a workspace name, a note.
 *
 * Do NOT use it around text the app itself wrote — a column header, a status, a category
 * label from a fixed list, a formatted currency figure or date, an id. Those cannot
 * contain anything to isolate, and wrapping them buries the signal this element is meant
 * to carry: seeing `<UserText>` in a diff should mean "this value came from outside".
 *
 * ## Layout
 *
 * It renders inline, so it does not disturb layout — but a `truncate` (or any other
 * `overflow` rule) needs a block or inline-block box to act on. Keep the existing
 * `<span className="truncate">` and put this INSIDE it, rather than moving the class
 * across onto this. Every call site in the app does exactly that, which is why this
 * takes no `className`: offering one would invite precisely the move that silently stops
 * a name truncating, and a `<bdi>` that needs styling can always be wrapped.
 */
export function UserText({ children }: { children?: ReactNode }) {
  return <bdi>{children}</bdi>;
}
